import { CdkDragDrop, CdkDragStart, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, switchMap, take, tap } from 'rxjs';

import {
  InspectionType,
  ScoreWeightModes,
  ScoringModels,
} from '@main-application/inspections/models/rest-inspections-model.interface';
import { loadCustomFields, loadTemplatePlaceholders } from '@main-application/management/store/management.actions';
import { TemplateEditorBase } from '@main-application/shared/template-editor-dialog/components/template-editor-dialog/template-editor-base/template-editor-base.component';
import {
  AreaModel,
  AreaPageModel,
  RepeatableArea,
  TemplateEditorInstructionsDefaultValue,
} from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { ButtonType } from '@shared/enums/button-type';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { getNextNumberItemName } from '@shared/functions/get-next-number-item-name';
import { ConfirmationModalData } from '@shared/interfaces/modal-data';
import { SubjectsService } from '@shared/services/subjects.service';
import { TemplateManagerService } from '@template/components/templates-manager/template-manager.service';
import {
  RestInspectionTemplateCategoryModel,
  RestTemplateAreaModel,
  RestTemplateModel,
} from '@template/models/rest-template-model.interface';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import { TemplateEditorAreaComponent } from './template-editor-area/template-editor-area.component';
import { TemplateEditorDialogHeaderComponent } from './template-editor-header/template-editor-header.component';
import { TemplateEditorContent } from '../../models/content-god-object';
import { getInitialAreaData, scrollIfOutOfDialog } from '../../models/share-methods.function';
import { DndExpandableService } from '../../services/dnd-expandable.service';
import { CommonExpandWorker } from '../../services/expand-worker';
import { ScoringModelsService } from '../../services/scoring-models.service';
import { TemplateEditorDialogService } from '../../services/template-editor-dialog.service';

export type TemplateEditorDialogData = {
  templateId: number;
  templateHeader?: string;
  isGlobal?: boolean;
  isEditable: boolean;
  highLightHeader?: boolean;
  isPropupAdmin?: boolean;
  fromBoard?: boolean;
};

export interface TemplateEditorDialogResult {
  inspectionTemplateId?: number;
  inspectionTemplateName?: string;
}

const TEMPLATE_RELOAD_ACTION = 'template-reload-action';

@UntilDestroy()
@Component({
  selector: 'app-template-editor-dialog',
  templateUrl: './template-editor-dialog.component.html',
  styleUrls: ['./template-editor-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorDialogComponent extends TemplateEditorBase implements OnInit, AfterViewInit {
  expandDuration = this.dndExpandableService.expandDurationMs;
  scrollSpeed = this.dndExpandableService.scrollSpeed;
  readonly ButtonType = ButtonType;
  readonly EIcon = EIcon;
  readonly Math = Math;
  readonly EColorPalette = EColorPalette;
  protected TemplateEditorInstructionsDefaultValue = TemplateEditorInstructionsDefaultValue;

  inspectionTemplateContent: TemplateEditorContent = new TemplateEditorContent();
  isBlockDraggable = false;
  isLoading$ = new BehaviorSubject<boolean>(true);
  uploadingXls = false;
  inspectionTemplate: RestTemplateModel;
  actionItemMenuOpen = false;

  isScoringParametersExist = false;
  welcomeInstructionsEditMode = false;
  welcomeInstructionsFocused = false;
  welcomeInstructionsText = '';
  expandWorker: CommonExpandWorker;
  actionInspectionMenuOpen = false;
  scoringForm: FormGroup;
  scoringModelItems = [
    { label: 'No Scoring', value: ScoringModels.None },
    { label: 'Maximum 100', value: ScoringModels.Maximum100 },
  ];
  scoreWeightItems = [];

  initFinished = false;
  displayedAreas: AreaModel[] = [];
  displayInterval: ReturnType<typeof setInterval>;
  chunkLength = 2;
  chunkIndex = 0;
  uploadInProgress$ = new BehaviorSubject<boolean>(false);
  downloadInProgress$ = new BehaviorSubject<boolean>(false);
  emitAreaSave$ = new BehaviorSubject<number>(null);
  emitItemSave$ = new BehaviorSubject<string>(null);
  focusedAreaId: number;
  focusedItemId: string;
  closeDialog: boolean;
  templateCategories$ = new BehaviorSubject<RestInspectionTemplateCategoryModel[] | null>(null);

  @ViewChild('fileUpload', { static: true }) fileUploader: ElementRef;
  @ViewChild(MatMenuTrigger) matMenuTrigger: MatMenuTrigger;
  @ViewChildren(TemplateEditorAreaComponent) inspectionItems!: QueryList<TemplateEditorAreaComponent>;
  @ViewChild(TemplateEditorDialogHeaderComponent) header: TemplateEditorDialogHeaderComponent;
  @ViewChild('areasDropListGroup') areasDropListGroup!: CdkDropList;
  @Output() scoringChanged = new EventEmitter<ScoringModels>();

  constructor(
    public dialogRef: MatDialogRef<TemplateEditorDialogComponent, TemplateEditorDialogResult>,
    store: Store<{}>,
    protected cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private templateEditorService: TemplateEditorDialogService,
    private templateManagerService: TemplateManagerService,
    private scoringModelsService: ScoringModelsService,
    private modalsService: ModalsService,
    private dndExpandableService: DndExpandableService,
    snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: TemplateEditorDialogData,
    private subjectsService: SubjectsService
  ) {
    super(store, snackbarService);
  }

  public get isScoringEnabled(): boolean {
    return this.inspectionTemplate?.scoringModel !== ScoringModels.None;
  }

  public get isReportEnabled(): boolean {
    return this.inspectionTemplate?.isNSPIRECompatible;
  }

  public get isWelcomeInstructionsEnabled(): boolean {
    return this.inspectionTemplate?.showInstructions;
  }

  public get isWelcomeInstructionsDisabled(): boolean {
    return this.inspectionTemplateContent?.areas?.length === 0 && !this.isWelcomeInstructionsEnabled;
  }

  ngOnInit(): void {
    this.store.dispatch(loadCustomFields());
    this.store.dispatch(loadTemplatePlaceholders());

    this.onGetTemplate();
    this.onTemplateCategories();

    this.dndExpandableService.dndAreaEvent$
      .pipe(untilDestroyed(this))
      .subscribe(e => {
        if (e === 'end') return;
        setTimeout(
          () => this.dndExpandableService.fixDndCache(this.areasDropListGroup._dropListRef),
          this.dndExpandableService.expandDuration
        );
      })
      .untilDestroyed(this);

    this.dialogRef
      .backdropClick()
      .subscribe(() => this.cancel())
      .untilDestroyed(this);

    this.dialogRef
      .keydownEvents()
      .pipe(filter(event => event.key === 'Escape'))
      .subscribe(() => this.cancel())
      .untilDestroyed(this);

    this.snackbarService.snackBarActions$
      .pipe(
        untilDestroyed(this),
        tap(({ action }) => {
          if (action === TEMPLATE_RELOAD_ACTION) {
            this.isLoading$.next(true);
            this.snackbarService.resetSnackbarActions();
            this.onGetTemplate();
          }
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.scoringModelsService.scoringModel$
      .pipe(untilDestroyed(this))
      .subscribe(model => {
        this.cdr.detectChanges();
      })
      .untilDestroyed(this);
  }

  onScoringModelChange(newModel: ScoringModels): void {
    this.scoringModelsService.setScoringModel(newModel);
  }

  ngAfterViewInit() {
    if (this.data.highLightHeader) {
      setTimeout(() => {
        this.header?.enterEditMode();
      }, 0);
    }
  }

  onGetTemplate(): void {
    (this.data.isGlobal
      ? this.templateManagerService.getTemplate(this.data.templateId)
      : this.templateEditorService.getTemplate(this.data.templateId)
    )
      .pipe(untilDestroyed(this))
      .subscribe(template => this.initTemplate(template))
      .untilDestroyed(this);
  }

  initTemplate(template: RestTemplateModel) {
    if (!template) return;

    if (template?.inspectionType !== InspectionType.CustomInspection) {
      this.modalsService.openInfoModal({
        title: 'Sorry!',
        content: `${template.name} can’t be edited, please contact support if you need to adjust it.`,
        buttonText: 'OK',
      });
      this.cancel();
      return;
    }
    this.inspectionTemplate = template;
    this.isScoringParametersExist = this.inspectionTemplate.scoringModel > 0;

    this.scoringForm = this.fb.group({
      scoringModel: [this.inspectionTemplate?.scoringModel || ScoringModels.None],
    });

    this.scoringForm
      .get('scoringModel')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => this.scoringChange(value))
      .untilDestroyed(this);

    this.inspectionTemplateContent.init(template);
    this.welcomeInstructionsText = template?.instructionsText ?? '';
    this.startDisplayInChunks();
    if (this.scoringForm.get('scoringModel').value === ScoringModels.Maximum100) {
      this.initScoringWeights();
    }
    this.scoringModelsService.combinedWeights$
      .pipe(untilDestroyed(this))
      .subscribe(customWeights => {
        const combinedWeights = [
          ...this.scoreWeightItems.filter(item => item.value !== 0),
          ...customWeights.map(weight => ({ label: `${weight}%`, value: weight })),
        ];
        this.scoreWeightItems = combinedWeights;
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);

    this.isLoading$.next(false);
    this.uploadingXls = false;
  }

  startDisplayInChunks(): void {
    this.displayNextChunk();
    this.displayInterval = setInterval(() => {
      if (this.chunkIndex * this.chunkLength < this.inspectionTemplateContent.areas.length) {
        this.displayNextChunk();
      } else {
        clearInterval(this.displayInterval);
      }
    }, 500);

    setTimeout(() => {
      this.initFinished = true;
    }, 510);
  }

  displayNextChunk(): void {
    const start = this.chunkIndex * this.chunkLength;
    const end = start + this.chunkLength;
    const nextChunk = this.inspectionTemplateContent.areas.slice(start, end);
    this.displayedAreas = [...this.displayedAreas, ...nextChunk];
    this.chunkIndex++;
    this.cdr.markForCheck();
  }

  initScoringWeights = () => {
    const standardWeights = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];
    const otherWeights = new Set<number>();

    this.inspectionTemplateContent.areas.forEach((area: AreaModel) => {
      if (area.scoreWeight && !standardWeights.includes(area.scoreWeight)) {
        otherWeights.add(area.scoreWeight);
      }

      area.areaSurvey.pages.forEach((page: AreaPageModel) => {
        if (page.scoreWeight && !standardWeights.includes(page.scoreWeight)) {
          otherWeights.add(page.scoreWeight);
        }
      });
    });

    Array.from(otherWeights).forEach(weight => {
      this.scoringModelsService.addCustomWeight(weight);
    });
  };

  add(isParentless = false) {
    const title = getNextNumberItemName(
      isParentless ? 'Item ' : 'Area ',
      this.inspectionTemplateContent.areas,
      e => e.title
    );
    this.templateEditorService.itemFocused$
      .pipe(take(1))
      .subscribe(focusItem => {
        const area: RestTemplateAreaModel = {
          title,
          scoreWeightMode: ScoreWeightModes.Automatic,
          scoreWeight: 100,
          areaSurveyJson: JSON.stringify(getInitialAreaData(title, isParentless)),
          position: focusItem != null ? focusItem.position + 1 : this.inspectionTemplateContent.areas.length,
        } as RestTemplateAreaModel;

        this.addInternal(area);
      })
      .untilDestroyed(this);
  }

  clone(area: AreaModel) {
    const { areaSurvey, ...props } = this.inspectionTemplateContent.getClonedArea(area);
    const clonedArea: RestTemplateAreaModel = {
      ...props,
      areaSurveyJson: JSON.stringify(areaSurvey),
      position: area.position + 1,
    } as RestTemplateAreaModel;

    this.addInternal(clonedArea);
  }

  private addInternal(area: RestTemplateAreaModel) {
    if (!this.checkEditRight()) {
      return;
    }

    area.inspectionTemplateId = this.inspectionTemplate.id;

    const tempId = Math.min(0, ...this.inspectionTemplateContent.areas.map(e => -e.id)) - 1;
    this.inspectionTemplateContent.addArea({ ...area, id: tempId });
    this.displayedAreas = this.inspectionTemplateContent.areas;
    this.cdr.markForCheck();
    setTimeout(() => {
      const areaId = `template-area-${Math.abs(tempId)}`;
      const element = document.getElementById(areaId);
      if (element) {
        scrollIfOutOfDialog(element);
      }
    }, 1000);

    this.templateEditorService
      .updateAreasPosition(this.inspectionTemplateContent.areas, TEMPLATE_RELOAD_ACTION, this.data.isGlobal ?? false)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: responses => {
          responses.forEach(r => {
            if (r) {
              const [id, restArea] = r;
              this.inspectionTemplateContent.replaceTempAreaWithRestArea(tempId, restArea);
              this.displayedAreas = this.inspectionTemplateContent.areas;
              this.cdr.markForCheck();
              setTimeout(() => {
                const item = this.inspectionItems.find(c => c.area.id === id);
                item?.enterEditMode();
                const element = (item as any)?.__ngContext__?.native as HTMLElement;
                if (element) scrollIfOutOfDialog(element);
              });
            }
          });
        },
        error: err => {
          console.error(err);
          this.inspectionTemplateContent.areas = this.inspectionTemplateContent.areas.filter(el => el.id !== tempId);
          this.displayedAreas = this.inspectionTemplateContent.areas;
        },
      })
      .untilDestroyed(this);
  }

  enterPressed(item: AreaModel) {
    if (!this.checkEditRight()) {
      return;
    }
    const index = this.inspectionTemplateContent.areas.findIndex(itemInner => itemInner.id === item.id);
    if (index === this.inspectionTemplateContent.areas.length - 1) {
      const isParentless = item.areaSurvey.pages.length === 1 && !item.areaSurvey.pages[0].pageType;
      this.add(!!isParentless);
    } else {
      const id = this.inspectionTemplateContent.areas[index + 1].id;
      const selectedItem = this.inspectionItems.find(c => c.area.id === id);
      selectedItem?.enterEditMode();

      const element = (selectedItem as any)?.__ngContext__?.native as HTMLElement;
      if (element) scrollIfOutOfDialog(element);
    }
  }

  trackBy(_index: number, item: AreaModel) {
    return item?.id;
  }

  dropDnd(event: CdkDragDrop<unknown, unknown, AreaModel>) {
    if (!this.checkEditRight()) {
      return;
    }
    this.dndExpandableService.endDnd(true);
    if (event.container != event.previousContainer) {
      return;
    }
    if (event.previousIndex == event.currentIndex) {
      return;
    }
    moveItemInArray(this.inspectionTemplateContent.areas, event.previousIndex, event.currentIndex);
    this.displayedAreas = this.inspectionTemplateContent.areas;
    this.templateEditorService
      .updateAreasPosition(this.inspectionTemplateContent.areas, TEMPLATE_RELOAD_ACTION, this.data.isGlobal ?? false)
      .subscribe()
      .untilDestroyed(this);
  }

  startDnd(event: CdkDragStart<AreaModel>) {
    if (!this.checkEditRight()) {
      return;
    }
    this.dndExpandableService.startDnd(event, true);
  }

  remove(item: AreaModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.templateEditorService
      .removeArea(item.id, this.data.isGlobal ?? false)
      .subscribe()
      .untilDestroyed(this);
    this.inspectionTemplateContent.removeArea(item.id);
    this.displayedAreas = this.inspectionTemplateContent.areas;
    this.dndExpandableService.viewportZoneChanging.next(null);
  }

  repeatableChange([type, id]: [RepeatableArea, number]) {
    if (!this.checkEditRight()) {
      return;
    }

    const handleArrayField = (
      field:
        | 'additionalBedroomInspectionTemplateAreaIds'
        | 'additionalBathroomInspectionTemplateAreaIds'
        | 'additionalHalfBathRoomInspectionTemplateAreaIds'
        | 'additionalAnyBathRoomInspectionTemplateAreaIds'
        | 'postCompleteSurveyInspectionTemplateAreaIds',
      currentValue: number[] | null | undefined
    ) => {
      const array = currentValue ? [...currentValue] : [];
      const index = array.indexOf(id);

      if (index >= 0) {
        array.splice(index, 1);
      } else {
        array.push(id);
      }

      this.inspectionTemplate[field] = array.length > 0 ? array : null;
    };

    const handleUnitDecisionField = () => {
      if (this.inspectionTemplate.nspireUnitDecisionAreaId === id) {
        this.inspectionTemplate.nspireUnitDecisionAreaId = null;
      } else {
        this.inspectionTemplate.nspireUnitDecisionAreaId = id;
      }
    };

    switch (type) {
      case RepeatableArea.Bathroom:
        handleArrayField(
          'additionalBathroomInspectionTemplateAreaIds',
          this.inspectionTemplate.additionalBathroomInspectionTemplateAreaIds
        );
        break;

      case RepeatableArea.PostCompleteSurvey:
        handleArrayField(
          'postCompleteSurveyInspectionTemplateAreaIds',
          this.inspectionTemplate.postCompleteSurveyInspectionTemplateAreaIds
        );
        break;

      case RepeatableArea.Bedroom:
        handleArrayField(
          'additionalBedroomInspectionTemplateAreaIds',
          this.inspectionTemplate.additionalBedroomInspectionTemplateAreaIds
        );
        break;

      case RepeatableArea.HalfBathroom:
        handleArrayField(
          'additionalHalfBathRoomInspectionTemplateAreaIds',
          this.inspectionTemplate.additionalHalfBathRoomInspectionTemplateAreaIds
        );
        break;

      case RepeatableArea.AnyBathRoom:
        handleArrayField(
          'additionalAnyBathRoomInspectionTemplateAreaIds',
          this.inspectionTemplate.additionalAnyBathRoomInspectionTemplateAreaIds
        );
        break;

      case RepeatableArea.NSpireUnit:
        handleUnitDecisionField();
        break;
    }

    this.inspectionTemplate = { ...this.inspectionTemplate };
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  areaChange(item: AreaModel) {
    if (item?.id && item.id < 0) return;
    if (!this.initFinished) return;
    if (!this.checkEditRight()) {
      return;
    }
    this.templateEditorService
      .updateArea(item, this.data.isGlobal ?? false, TEMPLATE_RELOAD_ACTION)
      .subscribe(() => {
        if (this.closeDialog) this.dialogRef.close();
      })
      .untilDestroyed(this);
    this.inspectionTemplateContent.updateArea(item);
    this.dndExpandableService.viewportZoneChanging.next(null);
  }

  setFocusedAreaId(id: number): void {
    this.focusedAreaId = id;
  }

  setFocusedItemId(id: string): void {
    this.focusedItemId = id;
  }

  removeTemplate() {
    if (!this.checkEditRight()) {
      return;
    }
    const data: ConfirmationModalData = {};
    this.templateEditorService
      .openRemovalTemplateModal(this.inspectionTemplate, data, this.data.fromBoard)
      .afterClosed()
      .pipe(filter(result => result === DialogResult.Success))
      .subscribe(() => {
        this.templateEditorService
          .removeTemplate(this.inspectionTemplate)
          .subscribe(() => this.dialogRef.close())
          .untilDestroyed(this);
      })
      .untilDestroyed(this);
  }

  headerChange(header: string) {
    if (!this.checkEditRight()) {
      return;
    }
    this.inspectionTemplate.name = header;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  descriptionChange(description: string) {
    if (!this.checkEditRight()) {
      return;
    }
    this.inspectionTemplate.description = description;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  residentSignatureChange() {
    if (!this.checkEditRight()) {
      return;
    }
    this.inspectionTemplate.residentSignatureRequired = !this.inspectionTemplate.residentSignatureRequired;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  pmSignatureChange() {
    if (!this.checkEditRight()) {
      return;
    }
    this.inspectionTemplate.hidePropertyManagerSignature = !this.inspectionTemplate.hidePropertyManagerSignature;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  showDefaultAnswersChange() {
    if (!this.checkEditRight()) {
      return;
    }
    this.inspectionTemplate.showDefaultAnswersInTheReport = !this.inspectionTemplate.showDefaultAnswersInTheReport;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  toggleNSpireReport() {
    if (!this.checkEditRight() || !this.data.isPropupAdmin) {
      return;
    }
    this.inspectionTemplate.isNSPIRECompatible = !this.inspectionTemplate.isNSPIRECompatible;
    if (this.inspectionTemplate.isNSPIRECompatible === false) {
      this.inspectionTemplate.nspireUnitDecisionAreaId = null;
    }
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  welcomeInstructionsChange() {
    if (!this.checkEditRight()) {
      return;
    }
    if (this.inspectionTemplate.showInstructions) {
      this.inspectionTemplate.showInstructions = false;
    } else {
      this.inspectionTemplate.showInstructions = true;
    }
    this.cdr.markForCheck();
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  onEditTemplateCategory(category: RestInspectionTemplateCategoryModel): void {
    if (!this.checkEditRight()) {
      return;
    }
    this.inspectionTemplate.inspectionTemplateCategoryId = category.id;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe()
      .untilDestroyed(this);
  }

  inspectionScoringChange() {
    if (!this.checkEditRight()) {
      return;
    }
    if (this.inspectionTemplate.scoringModel === ScoringModels.None) {
      this.scoringForm.get('scoringModel').setValue(ScoringModels.Maximum100);
    } else {
      this.scoringForm.get('scoringModel').setValue(ScoringModels.None);
    }
  }

  scoringChange(value: any) {
    if (!this.checkEditRight()) {
      return;
    }
    const selectedScoringModel = value as ScoringModels;
    if (selectedScoringModel !== undefined && selectedScoringModel !== null) {
      this.inspectionTemplate.scoringModel = selectedScoringModel;
      this.templateEditorService
        .editTemplate(this.inspectionTemplate, this.data.isGlobal)
        .subscribe()
        .untilDestroyed(this);

      this.scoringModelsService.setScoringModel(selectedScoringModel);
    }
    this.cdr.detectChanges();
  }

  cancel() {
    setTimeout(() => {
      if (this.welcomeInstructionsFocused) {
        this.leaveInstructionsEditMode(true);
      } else if (this.focusedAreaId) {
        this.emitAreaSave$.next(this.focusedAreaId);
        this.focusedAreaId = null;
        this.closeDialog = true;
      } else if (this.focusedItemId) {
        this.emitItemSave$.next(this.focusedItemId);
        this.focusedItemId = null;
        this.closeDialog = true;
      } else {
        this.dialogRef.close();
      }
    });
  }

  checkEditRight(notify = true): boolean {
    if (!this.initFinished) return;
    return super.checkEditRight(this.data.isEditable, notify);
  }

  onMatDialogScroll(event: Event) {
    this.dndExpandableService.viewportZoneChanging.next(null);
  }

  downloadFile() {
    this.downloadInProgress$.next(true);
    this.templateEditorService
      .downloadTemplateFile(this.inspectionTemplate.id)
      .subscribe(data => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = this.inspectionTemplate.name + '.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        this.downloadInProgress$.next(false);
      })
      .untilDestroyed(this);
  }

  uploadFile() {
    this.fileUploader.nativeElement.value = '';
    this.fileUploader.nativeElement.click();
  }

  fileChange(event: Event) {
    this.uploadInProgress$.next(true);
    const file = (event.target as HTMLInputElement).files[0];
    this.uploadingXls = true;
    this.templateEditorService
      .uploadTemplateFile(this.inspectionTemplate.id, file)
      .pipe(
        tap(result => {
          this.uploadInProgress$.next(false);
          if (result?.errors?.length > 0) {
            this.modalsService.openInfoModal({
              title: 'Awww shucks. Import failed 💩',
              content: result.errors[0].message ?? 'Your import failed',
            });
            this.uploadingXls = false;
          }
        }),
        filter(result => result.state === 'Success'),
        tap(() => {
          this.isLoading$.next(true);
          this.cdr.markForCheck();
        }),
        switchMap(() => this.templateEditorService.getTemplate(this.inspectionTemplate.id))
      )
      .subscribe(template => this.initTemplate(template))
      .untilDestroyed(this);
  }

  enterInstructionsEditMode() {
    if (!this.checkEditRight()) {
      return;
    }
    this.welcomeInstructionsEditMode = true;
    this.welcomeInstructionsFocused = true;
    this.cdr.detectChanges();
  }

  leaveInstructionsEditMode(closeDialog = false) {
    if (!this.checkEditRight()) {
      return;
    }
    this.welcomeInstructionsEditMode = false;
    const getValue = this.welcomeInstructionsText?.trim() ?? '';
    this.inspectionTemplate.instructionsText = getValue;
    this.templateEditorService
      .editTemplate(this.inspectionTemplate, this.data.isGlobal)
      .subscribe(() => {
        if (closeDialog || this.closeDialog) this.dialogRef.close();
        this.closeDialog = false;
      })
      .untilDestroyed(this);
  }

  onSaveArea(): void {
    this.subjectsService.templatesFocusOutEditor$.next();
  }

  private onTemplateCategories(): void {
    this.templateEditorService
      .getInspectionTemplateCategories()
      .subscribe(res => {
        res?.sort((a, b) => a.name?.toLocaleLowerCase().localeCompare(b.name?.toLocaleLowerCase()));
        this.templateCategories$.next(res);
      })
      .untilDestroyed(this);
  }
}
